import type { ShareClassModel } from '../../../../../api/shareclass/types';
import type { FundData } from '../../../../../api/funds/types';

export enum KidDocumentStatus {
  Published = 'published',
  Reviewed = 'reviewed',
  Edited = 'edited',
  Editing = 'editing',
  Rejected = 'rejected',
  NoDocument = 'no-document',
  Deactivated = 'deactivated',
  FiguresAlert = 'figures-alert',
}

/** Human-readable label for each KID document status. */
export const KID_STATUS_LABEL: Record<KidDocumentStatus, string> = {
  [KidDocumentStatus.Published]: 'Published',
  [KidDocumentStatus.Edited]: 'Ready for Review',
  [KidDocumentStatus.Reviewed]: 'Ready to Publish',
  [KidDocumentStatus.Rejected]: 'Rejected',
  [KidDocumentStatus.NoDocument]: 'No Document',
  [KidDocumentStatus.Deactivated]: 'Deactivated',
  [KidDocumentStatus.Editing]: 'Editing',
  [KidDocumentStatus.FiguresAlert]: 'Figures Alert',
};

/**
 * Maps each KID document status to the Pill `status` prop value.
 * Keeps Pill colours consistent across overview table and share class table.
 */
export const KID_STATUS_TO_PILL: Record<KidDocumentStatus, string> = {
  [KidDocumentStatus.Published]: 'pass',
  [KidDocumentStatus.Edited]: 'review',
  [KidDocumentStatus.Reviewed]: 'review-complete',
  [KidDocumentStatus.Rejected]: 'fail',
  [KidDocumentStatus.NoDocument]: 'na',
  [KidDocumentStatus.Deactivated]: 'na',
  [KidDocumentStatus.Editing]: 'edit',
  [KidDocumentStatus.FiguresAlert]: 'alert',
};

/** Raw shape returned by the KID documents API — share class identity + document status only, no share class names. */
export interface KidShareClassRecord extends Pick<ShareClassModel, 'share_class_id' | 'share_class_isin'> {
  version_number: string;
  status: KidDocumentStatus;
  kid_is_active: boolean;
  document_language: string;
  description?: string;
}

/** Enriched UI model — KidShareClassRecord joined with ShareClassModel data to add human-readable names. */
export interface KidShareClass extends KidShareClassRecord {
  share_class_name?: ShareClassModel['share_class_name'];
  share_class_name_full?: ShareClassModel['share_class_name_full'];
  has_alert?: boolean;
}

export interface ShareClassAlertStatus {
  shareClassId: string;
  isin: string;
  hasAlert: boolean;
  alertType?: 'figures-alert' | 'other';
}

export type GetShareClassAlertsResponse = ShareClassAlertStatus[];

/** Raw shape returned by the KID documents API — fund + its share class document records. */
export interface KidFundRecord {
  fund_id: string;
  fund_name: string;
  fund_name_long: string;
  share_classes: KidShareClassRecord[];
}

/** Enriched UI model — KidFundRecord joined with FundData and ShareClassModel to add names and computed statuses. */
export interface KidFund extends Pick<FundData, 'fund_id' | 'fund_name' | 'fund_name_full'> {
  shareClasses: KidShareClass[];
  editStatus: 'editing' | 'published' | 'no-edits';
  reviewStatus: string;
  publishedStatus: string;
  totalShareClasses: number;
  completedShareClasses: number;
}